import {AxiosInstance} from 'axios';
import {isValid, parseISO} from 'date-fns';

import {
  axiBasketball,
  axiBoxing,
  axiDota2,
  axiFootball,
  axiMma,
  axiVolleyball,
  getDateFromIsoString,
  GetListOutput,
  GetListParams,
} from '@shared/api';
import {formatInTz} from '@shared/utils';
import {
  ApiSportsMappingsResponse,
  ApiSportsMappingValue,
  LeagueSeasonDTO,
  LeagueSeasonsResponse,
  MappingKind,
  MatchMappingDTO,
  SportMatchActivationItem,
  SportMatchDTO,
  SportMatchStatus,
} from './types';

const SPORT_AXIOS_MAP: Record<string, AxiosInstance> = {
  football: axiFootball,
  basketball: axiBasketball,
  volleyball: axiVolleyball,
  mma: axiMma,
  boxing: axiBoxing,
  dota2: axiDota2,
};

export const SPORT_CATEGORY_SLUGS = Object.keys(SPORT_AXIOS_MAP);

// Most sports have their own category, but esports disciplines (e.g. Dota 2)
// share the "Esports" category and are split into per-game services, so the
// subcategory slug picks the service. It takes priority over the category.
export const getSportServiceKey = (
  categorySlug: string | null | undefined,
  subcategorySlug: string | null | undefined,
): string | undefined => {
  const subcategory = subcategorySlug?.toLowerCase();
  if (subcategory && subcategory in SPORT_AXIOS_MAP) return subcategory;

  return categorySlug?.toLowerCase();
};

interface SportMatchActivationItemOnBackend {
  id: string;
  external_id: number;
  name: string;
  status: number;
  start_time: string | null;
  activated_at: string | null;
  tournament: {
    id: string;
    name: string;
  };
}

interface GetSportMatchesResponse {
  array: SportMatchActivationItemOnBackend[];
  page: number;
  per_page: number;
  total_pages: number;
  total: number;
}

export interface GetSportMatchesParams extends GetListParams {
  hideFinished?: boolean;
  activated?: boolean;
  tournamentId?: string;
  search?: string;
  startTimeFrom?: string;
  startTimeTo?: string;
}

const getSportAxios = (categorySlug: string): AxiosInstance => {
  const axi = SPORT_AXIOS_MAP[categorySlug];
  if (!axi) {
    throw new Error(
      `No sport service configured for category: ${categorySlug}`,
    );
  }

  return axi;
};

export const getSportMatches = async (
  categorySlug: string,
  {
    page,
    pageSize,
    hideFinished,
    activated,
    tournamentId,
    search,
    startTimeFrom,
    startTimeTo,
  }: GetSportMatchesParams = {},
): Promise<GetListOutput<SportMatchActivationItem>> => {
  const response = await getSportAxios(
    categorySlug,
  ).get<GetSportMatchesResponse>('/admin/matches', {
    params: {
      page,
      per_page: pageSize,
      hide_finished: hideFinished,
      activated,
      tournament_id: tournamentId,
      search,
      start_time_from: startTimeFrom,
      start_time_to: startTimeTo,
    },
  });

  const {
    array,
    page: responsePage,
    per_page,
    total_pages,
    total,
  } = response.data;

  return {
    items: array.map(match => ({
      id: match.id,
      externalId: match.external_id,
      name: match.name,
      status: match.status as SportMatchStatus,
      startTime: match.start_time
        ? getDateFromIsoString(match.start_time)
        : null,
      activatedAt: match.activated_at
        ? getDateFromIsoString(match.activated_at)
        : null,
      tournament: match.tournament,
    })),
    page: responsePage,
    pageSize: per_page,
    totalPages: total_pages,
    total,
  };
};

export const activateSportMatches = async (
  categorySlug: string,
  matchIds: string[],
): Promise<string[]> => {
  const {data} = await getSportAxios(categorySlug).post<{
    activated: string[];
  }>('/admin/matches/order', {match_ids: matchIds});

  return data.activated;
};

export const getSportMatch = async (
  categorySlug: string,
  matchId: string,
): Promise<SportMatchDTO> => {
  const response = await getSportAxios(categorySlug).get<SportMatchDTO>(
    `/admin/matches/${matchId}`,
  );
  return response.data;
};

// The dropdown renders `country` as the parenthetical detail; for matches we
// surface the kick-off date/time there.
const formatMatchDate = (startDate?: string | null): string => {
  if (!startDate) return '';
  const parsed = parseISO(startDate);
  return isValid(parsed) ? formatInTz(parsed, 'dd.MM.yyyy HH:mm') : '';
};

const matchToMappingValue = (
  match: MatchMappingDTO,
): ApiSportsMappingValue => ({
  id: match.id,
  name:
    match.title ||
    [match.home?.name, match.away?.name].filter(Boolean).join(' vs ') ||
    String(match.id),
  logo: '',
  country: formatMatchDate(match.start_date),
});

// The leagues/teams endpoints return a search list ({search_query, values}),
// while the matches endpoint returns a plain array of matches. Normalize both
// into ApiSportsMappingsResponse so every kind behaves as a search dropdown.
const normalizeMappingsResponse = (
  data: ApiSportsMappingsResponse | MatchMappingDTO[],
): ApiSportsMappingsResponse => {
  if (Array.isArray(data)) {
    return {
      search_query: '',
      values: data.map(match => matchToMappingValue(match)),
    };
  }

  if ('values' in data && Array.isArray(data.values)) {
    return {search_query: data.search_query ?? '', values: data.values};
  }

  return {search_query: '', values: []};
};

export const getMappings = async (
  categorySlug: string,
  kind: MappingKind,
  entityId: string,
  searchQuery?: string,
  season?: string,
): Promise<ApiSportsMappingsResponse> => {
  const axi = getSportAxios(categorySlug);

  // The matches endpoint narrows by season and takes no search query; the
  // leagues/teams endpoints are the other way round.
  const params: Record<string, string> = {};
  if (kind === 'matches') {
    if (season) params.season = season;
  } else if (searchQuery) {
    params.search_query = searchQuery;
  }

  const response = await axi.get<ApiSportsMappingsResponse | MatchMappingDTO[]>(
    `/admin/mappings/${kind}/${entityId}`,
    {params},
  );
  return normalizeMappingsResponse(response.data);
};

// league_id is the internal league UUID (SportMatchLeagueDTO.id), not the
// lsports competition id shown next to it.
export const getLeagueSeasons = async (
  categorySlug: string,
  leagueId: string,
): Promise<LeagueSeasonDTO[]> => {
  const axi = getSportAxios(categorySlug);
  const response = await axi.get<LeagueSeasonsResponse>(
    `/admin/stats/leagues/${leagueId}/seasons`,
  );
  return response.data.seasons ?? [];
};

export const updateMapping = async (
  categorySlug: string,
  kind: MappingKind,
  entityId: string,
  id: number,
): Promise<void> => {
  const axi = getSportAxios(categorySlug);
  await axi.patch(`/admin/mappings/${kind}/${entityId}`, {id});
};
